import React, {forwardRef, ReactElement} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Badge} from '../Badge'
import {Icon} from '../Icon'
import {Text} from '../Text'

const PILL_VARIANTS = ['default'] as const

export type PillVariant = (typeof PILL_VARIANTS)[number]

type Props = {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Pass through style to allow styles overrides */
  style?: React.CSSProperties
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether the pill is active or not */
  isSelected?: boolean
  /** Pill content */
  children?: React.ReactNode
  /** Action for pill click */
  onClick?: (e?: React.SyntheticEvent) => void
  /** Icon left aligned */
  iconStart?: ReactElement
  /** Icon right aligned */
  iconEnd?: ReactElement
  /** Should show down arrow icon and border */
  showArrow?: boolean
  /** Weather the button is disabled or not */
  isDisabled?: boolean
  /** If a count should be displayed */
  count?: number
  /** Pill variant */
  variant?: PillVariant
  /** If should stretch to full width of parent */
  fullWidth?: boolean
  /** The size of the pill */
  size?: Extract<Size, 'sm' | 'lg'>
}

const createVariantStyles = (variant: string) => {
  const {unselected, selected} = cssTheme.colors.pill
  return `
    &.--variant-${variant} {
      color: ${unselected.content};
      background: ${unselected.background};
      outline-color: ${unselected.border};
      svg {
        color: ${unselected.content};
      }

      ${linariaMq.desktopXs} {
        &:hover,
        &:focus {
          background: ${unselected.hover.background};
          color: ${unselected.hover.content};
          outline-color: ${unselected.hover.border};
          svg {
            color: ${unselected.hover.content};
          }
        }

        &.--selected {
          &:hover,
          &:focus {
            background: ${selected.hover.background};
            color: ${selected.hover.content};
            outline-color: ${selected.hover.border};
            svg {
              color: ${selected.hover.content};
            }
          }
        }
      }

      &.--selected {
        color: ${selected.content};
        background: ${selected.background};
        outline-color: ${selected.border};
        outline-width: 2px;
        outline-offset: -2px;
        svg {
          color: ${selected.content};
        }
      }
    }
  `
}

const PillElement = styled('button')`
  display: flex;
  align-items: center;
  white-space: nowrap;
  border-radius: ${cssTheme.layout.radius.rounded};
  padding: ${cssTheme.layout.spacing.s250} ${cssTheme.layout.spacing.s400};
  outline: 1px solid transparent;
  outline-offset: -1px;
  border: none;
  cursor: pointer;
  gap: ${cssTheme.layout.spacing.s250};
  transition:
    background-color 0.15s ease-in-out,
    outline-color 0.15s ease-in-out,
    color 0.15s ease-in-out;

  ${PILL_VARIANTS.map(createVariantStyles).join('\n')}

  &:disabled {
    background-color: ${cssTheme.colors.pill.disabled.background} !important;
    outline-color: ${cssTheme.colors.pill.disabled.border} !important;
    color: ${cssTheme.colors.pill.disabled.content} !important;
    cursor: not-allowed;
    svg {
      color: ${cssTheme.colors.pill.disabled.content} !important;
    }
  }

  &.--full-width {
    width: 100%;
    justify-content: center;
  }
  &.--size-lg {
    padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
  }
`

export const IconWrapper = styled('span')``

export const DownArrowIconWrapper = styled('span')`
  display: flex;

  &.--variant-default {
    color: ${cssTheme.colors.pill.unselected.content};
    &.--selected {
      color: ${cssTheme.colors.pill.selected.content};
    }
  }
`

export const Pill = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      isSelected = false,
      onClick,
      dataId,
      iconStart = null,
      iconEnd = null,
      showArrow = false,
      isDisabled = false,
      count = 0,
      variant = 'default',
      fullWidth = false,
      className,
      size = 'sm',
      style
    },
    ref
  ) => {
    const handleClick = (event: React.SyntheticEvent) => {
      event.preventDefault()

      if (onClick) onClick()
    }

    return (
      <PillElement
        ref={ref}
        disabled={isDisabled}
        data-id={dataId}
        onClick={handleClick}
        className={cx(
          className,
          `--variant-${variant}`,
          isSelected && `--selected`,
          fullWidth && `--full-width`,
          `--size-${size}`
        )}
        style={style}
      >
        {iconStart && <IconWrapper>{iconStart}</IconWrapper>}
        {children && (
          <Text variant="labelS" tag="div">
            {children}
          </Text>
        )}
        {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
        {count > 0 && <Badge variant="brand">{count}</Badge>}
        {showArrow && (
          <DownArrowIconWrapper
            className={cx(`--variant-${variant}`, isSelected && `--selected`)}
          >
            <Icon name="CaratDown" size="xs" />
          </DownArrowIconWrapper>
        )}
      </PillElement>
    )
  }
)

Pill.displayName = 'Pill'
